import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && token !== 'undefined' && token !== 'null' && token.trim() !== '') {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No token found in localStorage for request:', config.url);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Helper function to extract data from API response
export const extractData = (response) => {
  // Backend returns: {success: true, data: {...}, message: "...", errors: null}
  // Axios wraps it in .data, so response.data = {success: true, data: {...}, ...}
  // We need response.data.data for the actual data
  if (response?.data?.data !== undefined) {
    return response.data.data;
  }
  // Fallback for direct data (shouldn't happen with our backend, but just in case)
  if (response?.data !== undefined) {
    return response.data;
  }
  return null;
};

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Skip refresh for auth endpoints to avoid infinite loops
      if (originalRequest.url?.includes('/auth/login') || 
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        // Clear auth and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken && refreshToken !== 'undefined' && refreshToken !== 'null') {
          // Use axios directly to avoid infinite loop with interceptors
          const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          }, {
            headers: {
              'Content-Type': 'application/json',
            },
          });
          
          const { access_token, refresh_token: newRefreshToken, user: userData } = response.data;
          
          // Update tokens and user
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', newRefreshToken);
          if (userData) {
            localStorage.setItem('user', JSON.stringify(userData));
          }
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } else {
          // No refresh token, clear auth and redirect
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // Refresh failed, clear auth and redirect
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  assignConstructor: (projectId, constructorId) => 
    api.post(`/projects/${projectId}/assign`, { constructor_id: constructorId }),
};

// Catalog API
export const catalogAPI = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/catalog`),
  create: (data) => api.post('/catalog', data),
  createBulk: (items) => api.post('/catalog/bulk', items),
  update: (id, data) => api.put(`/catalog/${id}`, data),
  delete: (id) => api.delete(`/catalog/${id}`),
};

// Quotes API
export const quotesAPI = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/quotes`),
  getMyQuotes: () => api.get('/quotes/my'),
  create: (data) => api.post('/quotes', data),
  accept: (id) => api.post(`/quotes/${id}/accept`),
  reject: (id) => api.post(`/quotes/${id}/reject`),
};

// Progress API
export const progressAPI = {
  getByProject: (projectId) => api.get(`/projects/${projectId}/progress`),
  getMyProgress: () => api.get('/progress/my'),
  create: (projectId, data) => api.post(`/projects/${projectId}/progress`, data),
  approve: (id) => api.post(`/progress/${id}/approve`),
  reject: (id) => api.post(`/progress/${id}/reject`),
};

// Evidence API
export const evidenceAPI = {
  getByProject: (projectId, type = null) => {
    const params = type ? `?evidence_type=${type}` : '';
    return api.get(`/projects/${projectId}/evidences${params}`);
  },
  upload: (projectId, formData) => 
    api.post(`/projects/${projectId}/evidences`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  delete: (id) => api.delete(`/evidences/${id}`),
  getFileUrl: (id) => `${API_URL}/api/v1/evidences/files/${id}`,
};

// Dashboard API
export const dashboardAPI = {
  getDeveloper: () => api.get('/dashboard/developer'),
  getConstructor: () => api.get('/dashboard/constructor'),
};

// Reports API
export const reportsAPI = {
  getProjectSummary: (projectId) => api.get(`/projects/${projectId}/reports/summary`),
  downloadProjectExcel: (projectId) => 
    api.get(`/projects/${projectId}/reports/excel`, { responseType: 'blob' }),
  downloadCatalogExcel: (projectId) => 
    api.get(`/projects/${projectId}/reports/catalog-excel`, { responseType: 'blob' }),
};

// Users API
export const usersAPI = {
  getConstructors: () => api.get('/constructors'),
};

export default api;
